import React, { useContext, useEffect, useState } from "react";
import useApi from "../Api";
import { ManagerContext } from "../utilities/manager";
import { Chip, CircularProgress, Menu, MenuItem } from "@mui/material";

type ManagerMenuProps = {
  instanceState: string | null;
  anchorEl: Element | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  onClose: () => void;
};

const ManagerMenu: React.FC<ManagerMenuProps> = ({
  instanceState,
  anchorEl,
  start,
  stop,
  onClose,
}) => {
  const { state, ping } = useContext(ManagerContext);

  return (
    <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={onClose}>
      <MenuItem onClick={() => ping().then(() => onClose())}>Ping</MenuItem>
      {instanceState === "Stopped" && (
        <MenuItem onClick={() => start().then(onClose)}>Start</MenuItem>
      )}
      {instanceState === "Running" && (
        <MenuItem onClick={() => stop().then(onClose)}>Stop</MenuItem>
      )}
    </Menu>
  );
};

type InstanceData = {
  state:
    | "Pending"
    | "Running"
    | "ShuttingDown"
    | "Stopped"
    | "Stopping"
    | "Terminated"
    | "Unknown";
};

const ManagerStatus: React.FC = () => {
  const { state: managerState, forget } = useContext(ManagerContext);
  const [instanceState, setInstanceState] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { get, post } = useApi();

  useEffect(() => {
    get("instance").then((data) => setInstanceState((data as any).state));
  }, [get]);

  if (managerState === "Unknown" && instanceState === null) {
    return <CircularProgress />;
  }

  async function start() {
    await post("instance/start");
    const intervalId = setInterval(() => {
      get<InstanceData>("instance").then((data) => {
        if (data.state === "Running") clearInterval(intervalId);
        setInstanceState(data.state);
      });
    }, 1000);
  }

  async function stop() {
    forget();
    await post("instance/stop");
    const intervalId = setInterval(() => {
      get<InstanceData>("instance").then((data) => {
        if (data.state === "Stopped") clearInterval(intervalId);
        setInstanceState(data.state);
      });
    }, 1000);
  }

  const openMenu: React.MouseEventHandler<HTMLDivElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const chip =
    managerState !== "Unknown" ? (
      <Chip label={managerState} onClick={openMenu} color="success" />
    ) : (
      <Chip label={instanceState} onClick={openMenu} color="error" />
    );

  return (
    <>
      {chip}
      <ManagerMenu
        instanceState={instanceState}
        anchorEl={anchorEl}
        start={start}
        stop={stop}
        onClose={() => setAnchorEl(null)}
      />
    </>
  );
};

export default ManagerStatus;
